import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import LayoutNavSectionList from 'components/layout_and_quote_builder/LayoutNavSectionList.jsx';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import QuoteBuilderManagePanel from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderManagePanel.jsx';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';
import QuoteBuilderNavSection from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderNavSection.jsx';
import PresentedDielineOptions from 'components/layout_and_quote_builder/quote_builder/PresentedDielineOptions.jsx';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import frankStyles from 'components/frank/_style.module.scss';
import MyEventListener from 'components/common/MyEventListener.jsx';
import { CacheProvider } from 'components/layout_and_quote_builder/quote_builder/CacheContext.jsx';
import ErrorBoundary from 'components/common/ErrorBoundary.jsx';
import { readSelectedManufacturingLineList, writeSelectedManufacturingLineList, clearSelectedManufacturingLineList }
  from 'components/frank/utils/manufacturingLineListUrlState.js';
import { clearSelectedSolution } from 'components/layout_and_quote_builder/utils/solutionUrlState.js';
import { linkedParentOf } from 'components/frank/utils/linkedNavSection.js';
import { DebugPopupProvider } from 'components/layout_and_quote_builder/DebugPopup.jsx';
import FrankSetupWizard from 'components/frank/FrankSetupWizard.jsx';
import InsideLaminationBody from 'components/frank/InsideLaminationBody.jsx';
import StandardManufacturingLinesToggle from 'components/frank/StandardManufacturingLinesToggle.jsx';
import PostRequest from 'components/common/PostRequest.jsx';
import GetRequest from 'components/common/GetRequest.jsx';
import { manufacturingLinesSource as readManufacturingLinesSource, setManufacturingLinesSource, STANDARD }
  from 'components/frank/utils/manufacturingLinesSource.js';
import { hydrateResponseCache, clearResponseCache } from 'components/frank/utils/ManufacturingLineResponseCache.js';

// How long the page will wait for the persisted response cache before rendering without it. Reading a quote's
// stored answers out of IndexedDB is a few milliseconds, so this is not a budget -- it is the guarantee that a
// wedged or blocked store (another tab holding an upgrade, a browser that stalls in private mode) costs the
// user a re-fetch and not a blank page.
const CACHE_HYDRATION_TIMEOUT_MILLISECONDS = 3000;

// Frank's tabbed front end for the Manufacturing Cost Calculator, served at /mcc. It reuses the same content
// components the legacy stacked page (ShowLayoutAndQuoteBuilderPage) renders -- they already save to the
// user_quote, handle read-only, currency and status -- but lays them out as tabs instead of one long page:
//   Manage (quantity / windows / currency + status) | one tab per nav-builder section (e.g. Carton) |
//   Customer (name / email / phone / notes) | Dieline (material + dimensions).
// The section tabs render LayoutNavSectionList with a single section, so the existing Frank-mode gating
// (fetch Frank's section-material-sheets endpoint, hide the manufacturing lines + solutions) carries over.
const FrankQuoteBuilderPage = ({
  config, navSections, dimensionGroups, layoutOptions, formulaOptions, optionsUserEntered, unit, locale,
  theme, btn_action, dimension_texts_on_layouts_enabled, user_quote_id, dieline_svg, header
}) => {
  const LayoutContextValues = {
    dimensionGroups, layoutOptions, formulaOptions, optionsUserEntered, unit, locale, theme, btn_action,
    dimension_texts_on_layouts_enabled,
  };

  // The tab bar is TWO levels. The TOP level is one tab per nav-builder part section (e.g. Carton) plus the
  // global Manage, Customer, Dieline and General -- a single row that does not wrap. The 'general' section is
  // the nav builder's catch-all, not a manufacturing part_type, so it gets no PART tab (it stays out of
  // priceableSections and so gets no Manufacturing Line -> Solution -> Pricing stages); instead it gets a
  // global tab alongside Manage / Customer / Dieline that renders its quote-wide pricing sub-sections (Profit
  // Margin, Transport, ...). The SECOND level, shown only for the active PART section, is that section's three
  // stages (Manufacturing Line -> Solution -> Pricing): the three views of its ManufacturingLineLists (the
  // listing, the solution cards, the priced view). The user picks a manufacturing line, then a solution, then
  // sees the price -- a sequence, so the stages are numbered.
  // Every part section the nav builder produced (all but its quote-wide 'general' catch-all), in nav-builder
  // order. İç sıvama (Inside lamination) is one of these -- it sits right after Hard cardboard -- so it takes a
  // top-level tab in this order; but it is PRICING-ONLY (no manufacturing line, no solution), so it is kept out
  // of priceableSections below (which drives the three wizard stages) and rendered like the general section.
  const partSections = navSections
    .map((section, index) => ({ section, index }))
    .filter(({ section }) => section.part_type !== 'general');

  const priceableSections = partSections
    .filter(({ section }) => section.part_type !== 'inside_lamination');

  // The nav builder's 'general' catch-all section -- its members are the quote-wide pricing sub-sections
  // (Profit Margin, Transport, ...), priced against the whole quote rather than one part. It carries its own
  // drawing as the item those sub-sections hang off (ManufacturingCostCalculatorNavBuilder#drawing_data).
  const generalSection = navSections.find((section) => section.part_type === 'general');

  // The three stage tabs a section is split into, in order. `stage` is the ManufacturingLineLists stageView.
  const sectionStageTabs = [
    { suffix: 'line', stage: 'line', labelKey: 'quote_builder.frank_tab_manufacturing_line' },
    { suffix: 'solution', stage: 'solution', labelKey: 'quote_builder.frank_tab_solution' },
    { suffix: 'pricing', stage: 'pricing', labelKey: 'quote_builder.frank_tab_pricing' },
  ];
  const sectionTabs = priceableSections.flatMap(({ section, index }) => sectionStageTabs.map((stageTab) => ({
    key: `section_${index}_${stageTab.suffix}`,
    label: `${section.label || section.part_type} - ${t(stageTab.labelKey)}`,
    sectionIndex: index,
    stage: stageTab.stage,
  })));

  // The quote total shown on the Total tab, kept here (not only inside the panel) so it can ride the tab label:
  // on load the active tab is a section, not Manage/Total, so no QuoteBuilderManagePanel is mounted to report it.
  const [totalPresented, setTotalPresented] = useState('');
  useEffect(() => {
    const refreshTotal = () => {
      if (user_quote_id == null) { return; }
      GetRequest({
        url: `/quote-builder/my-quotes/${user_quote_id}/summary`,
        callback: (data) => { setTotalPresented(data.total_presented || ''); },
      });
    };
    refreshTotal();
    // A pricing commit or a general-form edit fires section_result_updated; re-read the total so the tab keeps up.
    document.addEventListener('quote_builder.section_result_updated', refreshTotal);
    return () => { document.removeEventListener('quote_builder.section_result_updated', refreshTotal); };
  }, []);

  // The top-level tabs: ONE per priceable section (labelled by the section only -- the stage no longer rides
  // in the label, so "Carton for lamination" appears once, not three times), then the Profit & Additional
  // Costs tab (itself a section), then the global Customer and Dieline. This keeps the bar to a single row.
  const topTabs = [
    { key: 'manage', label: t('quote_builder.manage') },
    // One tab per part section, in nav-builder order. A wizard section (Carton, Hard cardboard, ...) opens its
    // three stages; İç sıvama (Inside lamination) is pricing-only, so it gets a plain global-style tab (no
    // sectionIndex -> no stage sub-tabs) that sits right after Hard cardboard and renders through renderGlobalTab.
    ...partSections.map(({ section, index }) => (
      section.part_type === 'inside_lamination'
        ? { key: `inside_lamination_${index}`, label: section.label || section.part_type, insideLaminationIndex: index }
        : { key: `section_${index}`, label: section.label || section.part_type, sectionIndex: index }
    )),
    // Its OWN tab label ("Profit & Additional Costs" / "Kar & Ek Maliyetler") -- names what the tab holds
    // (profit margin plus the additional cost lines), rather than reusing the section's generic "General" /
    // "Genel" label, which stays as-is for the legacy page. Only offered when the nav builder produced a
    // general section. Sits right after the part sections -- it is itself a section, so the section tabs stay
    // in order -- and before the global Customer and Dieline tabs.
    ...(generalSection ? [{ key: 'general', label: t('quote_builder.frank_tab_general') }] : []),
    { key: 'customer', label: t('quote_builder.customer') },
    { key: 'dieline', label: t('quote_builder.dieline') },
    // The cost summary (the Malzeme breakdown, the manufacturing/profit/total rows and the PDF) -- moved off the
    // Manage tab (which keeps only the quantity/window/currency form and the price status) onto its own tab,
    // last in the row. The quote total rides the label ("Toplam (USD 45.01)") so it is readable without opening
    // the tab; it falls back to the plain label until the summary loads, or when nothing is priced yet.
    {
      key: 'total',
      label: totalPresented
        ? `${t('quote_builder.frank_tab_total')} (${totalPresented})`
        : t('quote_builder.frank_tab_total'),
    },
  ];

  // The GLOBAL (non-section) tab keys, in bar order -- Manage / (General) / Customer / Dieline / Total. Like
  // the section bodies, each is kept mounted once visited: some of them fetch and price (the General tab pulls
  // its Transport + Profit-margin sub-sections and totals, the Total tab the quote summary), so remounting them
  // on every visit re-ran those and blanked their figures until the fetch returned. Kept mounted, they show
  // their already-loaded values at once.
  const globalTabKeys = topTabs.filter((tab) => tab.sectionIndex == null).map((tab) => tab.key);

  // Seed from the quote's own quantity (falling back to 1000, the create default), NOT a hardcoded 1000: the
  // server scores/solves against user_quote.count, and count is part of the ManufacturingLineResponseCache key,
  // so the page's count MUST match the quote's on load -- otherwise a reopened quote whose quantity is not 1000
  // would file its results under the wrong (count=1000) key. Mirrors FrankSetupWizard's seed.
  const [count, setCount] = useState((window.user_quote && window.user_quote.count) || 1000);
  // Bumped on a REAL order-count change (the count_changed listener below), part of the section key so the change
  // REMOUNTS the open section -- resetting its in-component picked line / marked solution and re-seeding it from
  // the state resetAllSectionSelections just cleared. count itself is a fetch dependency (re-solves in place) and
  // is NOT put in the key, so a transient/blank keystroke never remounts; only a settled, different count does.
  const [orderCountRevision, setOrderCountRevision] = useState(0);

  // The quote-wide manufacturing-lines source (the "Standard Manufacturing Lines" example account vs the owner's
  // own account). Seeded from the window global that frank/edit.html.erb rendered from the persisted column.
  const [manufacturingLinesSource, setSourceState] = useState(readManufacturingLinesSource());

  // CLEAR EVERY SECTION'S SELECTION, keeping at most one section's picked manufacturing line. The single "forget
  // every section's pick" routine, shared by the two quote-wide inputs that invalidate the sections' selections: a
  // MANUFACTURING-LINES SOURCE switch (changeManufacturingLinesSource) and an ORDER-COUNT change (the
  // count_changed listener). Both re-solve every section against a DIFFERENT problem -- a different set of machines
  // (the shared example account vs the owner's own), or a different order quantity -- so a manufacturing line and
  // solution picked against the old one mean nothing against the new one. The remount re-seeds each section from
  // the address bar and the saved quote, so leaving the old selection there restores a plan the new problem does
  // not list and a solution priced against it -- which is what confused the section after such a change. So drop
  // the picked line and chosen solution from the address bar and from this page's own mirrors, and clear the saved
  // solutions the sections re-seed from on remount. selected_lists is nulled, not set to [], to match the "no
  // selection" the server serializes on a fresh load -- so this optimistic state and the reloaded one read
  // identically. The general section is left alone: it holds the quote-wide pricing sub-sections, not a
  // manufacturing-line solution (as the server reset does too).
  //   keepLineForIndex / keepLineIdentifier keep ONE section's picked line through the reset -- used by the
  //   standard-lines fallback, which flips to Standard AND carries the line the owner borrowed so the section
  //   opens on its solutions rather than an empty slate (see useStandardLineForSection). A picked line lives in
  //   the address bar (not selected_lists), so seeding it here and nulling selected_lists agree: no solution is
  //   chosen yet, only a line.
  const resetAllSectionSelections = ({ keepLineForIndex = null, keepLineIdentifier = null } = {}) => {
    priceableSections.forEach(({ index }) => {
      clearSelectedSolution(index);
      if (index === keepLineForIndex) {
        writeSelectedManufacturingLineList(index, keepLineIdentifier);
      } else {
        clearSelectedManufacturingLineList(index);
      }
    });
    setPickedLineBySection(keepLineForIndex === null ? {} : { [keepLineForIndex]: keepLineIdentifier });
    setChosenSolutionBySection({});
    window.user_quote_nav_sections = (window.user_quote_nav_sections || []).map((section) => (
      section.part_type === 'general' ? section : { ...section, selected_lists: null }
    ));
  };

  // Update the global FIRST (every Frank URL builder reads it at fetch time), then the state -- which remounts the
  // sections via the key below, so they re-solve against the new source -- then persist the new default. The
  // listing is served live from the request param the fetches carry, so a failed persist only loses the stored
  // seed: the page already shows the new source and there is nothing to roll back. The matching server-side reset
  // rides this persist (frank#manufacturing_lines_source clears selected_lists when the source changes), so a
  // later reload stays on the clean slate too.
  const persistManufacturingLinesSource = (next) => {
    setManufacturingLinesSource(next);
    setSourceState(next);
    PostRequest({
      url: `/mcc/${window.user_quote_id}/manufacturing-lines-source`,
      data: { manufacturing_lines_source: next },
      // PostRequest calls `callback` unconditionally on a 2xx; without one a successful persist would throw. The
      // stored value is only the seed (the live answer rides the request param), so there is nothing to do with
      // the response.
      callback: () => {},
    }).catch(() => {});
  };

  // The quote-wide toggle: switch the source and clear the slate. (An owner switching to their own manufacturing
  // lines with no printing machines yet lands on an empty listing whose add-a-machine picker shows INLINE -- see
  // ManufacturingLineLists' empty state -- so there is no popup to open here.)
  const changeManufacturingLinesSource = (next) => {
    if (next === manufacturingLinesSource) { return; }

    resetAllSectionSelections();
    persistManufacturingLinesSource(next);
  };

  // BORROW A STANDARD LINE when the owner's OWN lines came up empty. The standard-lines fallback (shown under the
  // "no manufacturing lines match" finding, on the owner's own lines only) reports the line the owner picked; flip
  // the whole quote to Standard, keep THAT section on THAT line through the remount, and open its Solution stage --
  // so the borrowed line is priced against the Standard account it came from, and the quote's Standard toggle now
  // reads selected. The other sections are cleared like any source switch.
  const useStandardLineForSection = (sectionIndex, identifier) => {
    resetAllSectionSelections({ keepLineForIndex: sectionIndex, keepLineIdentifier: identifier });
    persistManufacturingLinesSource(STANDARD);
    openStage(sectionIndex, 'solution');
  };

  // When the catalogue the sections are priced against changes, re-solve them against the new catalogue. This
  // fires from two kinds of trigger. IN THIS PAGE: the owner finishing an edit in the filter island's
  // ManageOwnMachinesButton or the empty-state EmptyStateMachineAdder, each firing ONCE when its machine picker
  // CLOSES having made a change -- not on every save: a per-save re-solve remounts the very subtree that holds
  // the open picker and tears it down mid-edit, so the owner could not add several in a row. IN ANOTHER TAB:
  // this tab becoming visible/focused again after the owner edited their unit prices on the settings page.
  // Recompute the response-cache fingerprint (the browser reuses a stored answer only while it matches, and a
  // catalogue change moved it), point the page's globals at the fresh one, DROP the now-stale cached answers, then
  // tell the sections to re-fetch (a genuine miss, now the cache is cleared) -- and the server recomputes because
  // the same change moved its answer's digest. There are TWO ways to tell them, for two triggers:
  //   machinesRevision -- part of the section KEY, so bumping it REMOUNTS the section. An IN-PAGE edit (adding /
  //     pricing a machine here) wants that: a just-completed setup step re-seeds every bit of the section's state
  //     from the address bar cleanly.
  //   dataRevision -- a PROP the section's fetches depend on, NOT part of its key, so bumping it re-runs those
  //     fetches IN PLACE. A TAB RETURN that finds a real change (the owner edited prices in another tab) uses this:
  //     the section keeps its identity -- no blank, no scroll jump, the pricing tree stays put -- and just its
  //     data refreshes, with the normal brief in-place loading. Remounting an open page on tab return is the
  //     distracting blink this avoids.
  const [machinesRevision, setMachinesRevision] = useState(0);
  const [dataRevision, setDataRevision] = useState(0);
  // One refresh at a time: a single tab switch can fire visibilitychange AND focus, and an in-page edit can race
  // a focus. The in-flight request is already fetching the freshest fingerprint, so a second would only duplicate
  // the work.
  const fingerprintRefreshInFlight = useRef(false);
  // An inline empty-state add form (add machine / add material) is open in a section -- counted, since the
  // stacked page view can have more than one open at once. A SPECULATIVE visibility/focus refresh must not
  // remount the sections while one is (the remount tears the open form down mid-edit -- the owner tabs away and
  // back and lands on the re-solved listing). Such a refresh defers its remount here and it runs when the last
  // form closes. See the effect below and the frank:own-setup-open / -close events the adders fire.
  const ownSetupFormOpenCountRef = useRef(0);
  const pendingInPlaceRefreshRef = useRef(false);
  useEffect(() => {
    // The catalogue moved, so this quote's cached answers are stale. The response cache is keyed by request
    // URL -- which a price change does not move -- and nothing else evicts it, so the re-listed sections would
    // just re-serve the pre-change answers unless it is dropped here FIRST. clearResponseCache also drops this
    // quote's persisted (IndexedDB) copies; re-pointing persistence at the fresh fingerprint (hydrateResponseCache)
    // is what then stores the answers the remount fetches under the NEW stamp, so they are read back on the next
    // load rather than pruned as stale. Shared by every trigger below AND by the deferred replay when the last
    // add form closes, so "the catalogue moved" is handled the one way.
    const dropStaleAnswers = () => {
      clearResponseCache();
      if (window.frank_response_cache_fingerprint) {
        hydrateResponseCache({
          fingerprint: window.frank_response_cache_fingerprint,
          variant: window.frank_response_cache_variant,
          userId: window.frank_response_cache_user_id,
          quoteId: user_quote_id
        });
      }
    };
    // An in-page edit re-solves by REMOUNTING the section (see machinesRevision) -- the just-completed setup step
    // re-seeds the section's state from the address bar.
    const remountSections = () => { dropStaleAnswers(); setMachinesRevision((revision) => revision + 1); };
    // A tab return that found a real change re-fetches IN PLACE (see dataRevision) -- the section keeps its
    // identity and only its data refreshes, so there is no blank/scroll-jump blink on coming back to the tab.
    const refreshSectionsInPlace = () => { dropStaleAnswers(); setDataRevision((revision) => revision + 1); };

    // remountAlways distinguishes the two triggers. An in-page machine edit KNOWS the catalogue moved and always
    // wants the re-solve (and keeps the original behaviour of re-solving even if the fingerprint fetch fails). A
    // visibility/focus refresh runs speculatively -- most tab switches change nothing -- so it only refreshes in
    // place when the CATALOGUE actually moved in another tab (catalogue_stamp, not an in-page nav-section edit and
    // not the code deploy), leaving an unaffected tab untouched (one cheap GET, no re-fetch).
    const refreshResponseFingerprint = ({ remountAlways }) => {
      if (fingerprintRefreshInFlight.current) { return; }
      fingerprintRefreshInFlight.current = true;

      // no-store + XMLHttpRequest: force a fresh recompute (a cached fingerprint would be the stale one), and
      // identify as XHR like the rest of the app's JSON gets.
      fetch(`/mcc/${window.user_quote_id}/response-cache-fingerprint`,
        { cache: 'no-store', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          if (!data || !data.fingerprint) { return false; }
          // Refresh only on a CROSS-TAB CATALOGUE change -- the owner editing a machine price or adding/removing a
          // machine on the settings page in ANOTHER tab. catalogue_stamp is the subset of the fingerprint that can
          // move there. The REST of the quote's inputs -- a section's lamination / printing / wastage, the dieline
          // options -- are edited IN this page, where the change already re-prices in place; but they move
          // inputs_stamp too, so comparing inputs_stamp made every window focus after such an in-page edit (a
          // native <select> refocuses the window) look like an external change and tear the section down to re-list
          // and re-score it. A code deploy moves neither stamp, so it does not blink an open page either. Fall back
          // to inputs_stamp, then the whole fingerprint, on a server too old to send catalogue_stamp.
          const dataChanged = data.catalogue_stamp !== undefined
            ? data.catalogue_stamp !== window.frank_response_cache_catalogue_stamp
            : (data.inputs_stamp !== undefined
              ? data.inputs_stamp !== window.frank_response_cache_inputs_stamp
              : data.fingerprint !== window.frank_response_cache_fingerprint);
          // Keep the whole cache key in sync, not just the validity stamp: a machine edit does not move the
          // variant/user_id today, but reading them off the same response means the page cannot drift into
          // fetching under a new fingerprint while hydrating/persisting under a stale variant. Harmless when
          // nothing is remounted below: the cache reads the fingerprint captured at hydrate, not this global.
          window.frank_response_cache_fingerprint = data.fingerprint;
          window.frank_response_cache_inputs_stamp = data.inputs_stamp;
          window.frank_response_cache_catalogue_stamp = data.catalogue_stamp;
          window.frank_response_cache_variant = data.variant;
          window.frank_response_cache_user_id = data.user_id;
          return dataChanged;
        })
        .catch(() => false)
        .then((dataChanged) => {
          if (!remountAlways && !dataChanged) { return; }

          // Hold a SPECULATIVE in-place refresh while an inline add form is open: even an in-place data refresh
          // re-runs the section's fetches, which tears the open machine/price form down mid-edit (the owner tabs
          // away and back and lands on the re-solved listing). It is deferred and applied when the last form closes
          // (onOwnSetupClose). A genuine own-machines-changed (remountAlways -- a completed add) is exempt: that IS
          // the moment to re-solve.
          if (!remountAlways && ownSetupFormOpenCountRef.current > 0) {
            pendingInPlaceRefreshRef.current = true;
            return;
          }
          // An in-page edit remounts (re-seeds from the URL); a tab return refreshes in place (no blink).
          if (remountAlways) { remountSections(); } else { refreshSectionsInPlace(); }
        })
        .finally(() => { fingerprintRefreshInFlight.current = false; });
    };

    const onOwnMachinesChanged = () => {
      // A full re-solve is happening now, so any refresh deferred behind an open form is subsumed by it.
      pendingInPlaceRefreshRef.current = false;
      refreshResponseFingerprint({ remountAlways: true });
    };
    // The catalogue can change in ANOTHER tab: the owner manages their unit prices on the settings page while
    // this /mcc tab stays open. Nothing re-checks the fingerprint while a tab is open, so re-check it when the
    // tab is looked at again -- visibilitychange for a tab switch, focus for a side-by-side layout where this
    // window regains focus without ever being hidden.
    const onBecameVisible = () => {
      if (document.visibilityState !== 'visible') { return; }
      refreshResponseFingerprint({ remountAlways: false });
    };
    const onFocus = () => refreshResponseFingerprint({ remountAlways: false });

    // The inline empty-state adders announce when one opens/closes, so a speculative refresh holds its in-place
    // re-fetch rather than tearing the open form down. Counted, since the stacked page view can have several open
    // at once; when the last closes, any deferred refresh runs. It is always the tab-return path (remountAlways is
    // never deferred), so it replays in place.
    const onOwnSetupOpen = () => { ownSetupFormOpenCountRef.current += 1; };
    const onOwnSetupClose = () => {
      ownSetupFormOpenCountRef.current = Math.max(0, ownSetupFormOpenCountRef.current - 1);
      if (ownSetupFormOpenCountRef.current === 0 && pendingInPlaceRefreshRef.current) {
        pendingInPlaceRefreshRef.current = false;
        refreshSectionsInPlace();
      }
    };

    window.addEventListener('frank:own-machines-changed', onOwnMachinesChanged);
    window.addEventListener('frank:own-setup-open', onOwnSetupOpen);
    window.addEventListener('frank:own-setup-close', onOwnSetupClose);
    document.addEventListener('visibilitychange', onBecameVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('frank:own-machines-changed', onOwnMachinesChanged);
      window.removeEventListener('frank:own-setup-open', onOwnSetupOpen);
      window.removeEventListener('frank:own-setup-close', onOwnSetupClose);
      document.removeEventListener('visibilitychange', onBecameVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // How many solutions a section already has SAVED (its committed selected_lists). Read straight from the
  // quote the server sent, with no dependence on component state, so the initial tab below can consult it
  // before that state exists.
  const savedSolutionCountFor = (index) => {
    const section = (window.user_quote_nav_sections || []).find((each) => each.sort_order === index);
    return (section && section.selected_lists ? section.selected_lists : []).length;
  };

  // The stage a section opens on when it has no remembered one: a PRICED section straight on Pricing (its price
  // is what the user came for), an un-priced one on Manufacturing Line. Used for the initial tab and, later, for
  // reopening / holding a kept-mounted section on the right stage -- one rule, so the two cannot drift.
  const defaultStageFor = (index) => (savedSolutionCountFor(index) > 0 ? 'pricing' : 'line');

  // Open on the section a refresh / shared link picked a plan in (its manufacturing_line_<index> is in the
  // URL), so the restored selection reopens on its own section. Otherwise the first part section (e.g. Carton)
  // -- the manufacturing costs it lists are what the page is FOR, so it is what a user wants to see first. A
  // quote with no part section (only the nav builder's 'general' catch-all) has no section tab, and falls back
  // to Manage.
  const restoredSection = priceableSections.find(
    ({ index }) => readSelectedManufacturingLineList(index) !== null
  );
  const initialSectionIndex = restoredSection ? restoredSection.index : priceableSections[0]?.index;
  // A section that ALREADY HAS A SOLUTION opens straight on its Pricing stage: a refresh or a shared link that
  // carries a priced section lands on the price it was set up to show, not back at the start of the sequence.
  // Pricing is a valid landing stage precisely because a saved solution is what opens it (see stageIsOpen).
  const initialStage = initialSectionIndex != null ? defaultStageFor(initialSectionIndex) : 'line';
  const [activeTab, setActiveTab] = useState(
    initialSectionIndex != null ? `section_${initialSectionIndex}_${initialStage}` : 'manage'
  );

  // activeTab stays the single source of truth, a flat `section_<index>_<stage>` key (so the URL-restore,
  // initial-tab and wizard logic all keep working unchanged); the two-level bar is DERIVED from it. A
  // section's top tab is "active" when activeTab is any of that section's three stages.
  const activeSectionMatch = activeTab.match(/^section_(\d+)_(line|solution|pricing)$/);
  const activeSectionIndex = activeSectionMatch ? Number(activeSectionMatch[1]) : null;
  const activeStage = activeSectionMatch ? activeSectionMatch[2] : null;

  // WHICH SECTION BODIES ARE KEPT MOUNTED. A section's body is expensive to (re)build -- it re-fetches its
  // listing and, on the Pricing stage, re-POSTs select-solution, which re-prices the section on the server
  // (SelectedSolutionSections). So once a section has been opened it is rendered from here on, hidden (not
  // unmounted) while another tab is active, and shown again instantly when the user comes back -- no remount,
  // no re-fetch, no re-price, so the total no longer drops to nothing and climbs back on every tab round-trip.
  // Seeded lazily (only the section that first opens is mounted) so a multi-section quote does not price every
  // section on load; each section joins the set the first time it becomes active.
  const [visitedSections, setVisitedSections] = useState(
    () => new Set(initialSectionIndex != null ? [initialSectionIndex] : [])
  );
  useEffect(() => {
    if (activeSectionIndex === null || visitedSections.has(activeSectionIndex)) { return; }
    setVisitedSections((previous) => new Set(previous).add(activeSectionIndex));
  }, [activeSectionIndex]);

  // The same keep-mounted treatment for the GLOBAL tabs, kept apart so section indices and tab keys do not mix.
  // Seeded with the initial tab only when it is a global one (a normal load opens on a section), so no global
  // tab fetches on load until it is opened; each joins the set the first time it becomes active.
  const [visitedGlobalTabs, setVisitedGlobalTabs] = useState(
    () => new Set(activeSectionIndex === null ? [activeTab] : [])
  );
  useEffect(() => {
    if (activeSectionIndex !== null || visitedGlobalTabs.has(activeTab)) { return; }
    setVisitedGlobalTabs((previous) => new Set(previous).add(activeTab));
  }, [activeTab, activeSectionIndex]);

  // THE SECTION'S STAGES ARE A SEQUENCE, NOT THREE VIEWS. A solution is a way of making the section with ONE
  // manufacturing line, and a price is what THAT solution costs -- so neither means anything until the stage
  // before it has been answered, and the tabs say so by being disabled rather than by letting the user in and
  // showing them an empty panel.
  //
  // What each stage is answered BY:
  //   line     -- always open; it is where the sequence starts.
  //   solution -- a manufacturing line is picked for this section (mirrored in the address bar, so a refresh
  //               and a shared link restore it).
  //   pricing  -- a solution is chosen. Chosen, not yet priced: the Pricing tab is what COMMITS the marked
  //               card (see ManufacturingLineLists' pricing commit), so gating on "already priced" would lock
  //               the door from the inside and the user could never reach it.
  //
  // A LINKED section is left alone. It picks nothing and solves nothing -- both come from the section that
  // leads it -- so its tabs explain that instead, and its Pricing follows the solution it inherited.
  const [pickedLineBySection, setPickedLineBySection] = useState(() => priceableSections.reduce(
    (picked, { index }) => ({ ...picked, [index]: readSelectedManufacturingLineList(index) }), {}
  ));
  const [chosenSolutionBySection, setChosenSolutionBySection] = useState(() => priceableSections.reduce(
    (chosen, { index }) => ({ ...chosen, [index]: savedSolutionCountFor(index) > 0 }), {}
  ));

  // The section's own selection, or -- for a linked section -- the leading section's, which is where its
  // manufacturing line and solution actually come from.
  const stageSourceIndex = (index) => {
    const parent = linkedParentOf(window.user_quote_nav_sections, index);
    return parent ? parent.sort_order : index;
  };
  const stageIsOpen = (index, stage) => {
    if (stage === 'line') { return true; }
    const source = stageSourceIndex(index);
    if (stage === 'solution') {
      return Boolean(pickedLineBySection[source]) || chosenSolutionBySection[source] === true;
    }
    return chosenSolutionBySection[index] === true || chosenSolutionBySection[source] === true;
  };

  // İç sıvama (Inside lamination) shows the material dimension of the hard_cardboard section it FOLLOWS, so it
  // stays disabled until that section has a chosen solution to inherit -- the user cannot open it until then.
  // Reads the linked parent's chosen-solution state, which navSectionsUpdated / navSectionsReset keep current
  // (chosenSolutionBySection), so the tab enables the moment hard_cardboard is solved and disables again on reset.
  const insideLaminationEnabled = (index) => {
    const parent = linkedParentOf(window.user_quote_nav_sections, index);
    return Boolean(parent && chosenSolutionBySection[parent.sort_order] === true);
  };

  // The server is the authority on what is selected, and it says so through these two events: select-solution
  // publishes navSectionsUpdated, and a reset (including the one a changed manufacturing line triggers)
  // publishes navSectionsReset. Both refresh window.user_quote_nav_sections first, so re-reading it is what
  // keeps a tab from staying open on a solution that no longer exists.
  const rereadSavedSolutions = () => setChosenSolutionBySection(
    () => priceableSections.reduce((chosen, { index }) => ({ ...chosen, [index]: savedSolutionCountFor(index) > 0 }), {})
  );
  useEffect(() => {
    window.addEventListener('navSectionsUpdated', rereadSavedSolutions);
    window.addEventListener('navSectionsReset', rereadSavedSolutions);
    return () => {
      window.removeEventListener('navSectionsUpdated', rereadSavedSolutions);
      window.removeEventListener('navSectionsReset', rereadSavedSolutions);
    };
  }, []);

  // A MARK DOES NOT OUTLIVE THE VISIT IT WAS MADE IN. Choosing a solution card only MARKS it; the Pricing tab
  // is what commits it. Leaving the section unmounts its tree and the uncommitted mark goes with it -- so this
  // page's memory of it has to go too, or Pricing stays open on the strength of a choice that is no longer on
  // screen. Falling back to what the quote has SAVED is what leaves an already-priced section open.
  //
  // Keyed on the active SECTION, not the stage: the three stages share one mounted section (see the key on
  // LayoutNavSectionList), so walking Solution -> Pricing keeps the mark, which is the whole point of it.
  useEffect(() => { rereadSavedSolutions(); }, [activeSectionIndex]);

  // A STAGE CAN CLOSE UNDER THE USER'S FEET. Ticking a filter re-lists the section and drops the picked plan
  // with it, so someone standing on the Solution stage is suddenly standing on a stage with nothing to solve.
  // Walk them back to the Manufacturing Line stage rather than leave them on a closed tab being asked to go
  // and pick one -- the tab is disabled now, so that prompt is a dead end.
  useEffect(() => {
    if (activeSectionIndex === null || activeStage === null) { return; }
    if (stageIsOpen(activeSectionIndex, activeStage)) { return; }

    openStage(activeSectionIndex, 'line');
  }, [activeSectionIndex, activeStage, pickedLineBySection, chosenSolutionBySection]);

  // İç sıvama can be OPEN when its hard_cardboard parent is later reset (a source switch, a changed count): the
  // tab disables under the user, so hand them back to Manage rather than leave them on a disabled tab's body.
  useEffect(() => {
    const match = activeTab.match(/^inside_lamination_(\d+)$/);
    if (!match) { return; }
    if (insideLaminationEnabled(Number(match[1]))) { return; }

    setActiveTab('manage');
  }, [activeTab, chosenSolutionBySection]);

  // Remember which stage each section was last left on, so returning to a section reopens where the user was
  // rather than jumping back to Manufacturing Line. Defaults to the first stage ('line') for an unvisited one.
  const lastStageBySection = useRef({});
  // Reopening a section on a stage that has since been closed (its solution was invalidated while the user was
  // elsewhere) would land them on a disabled tab, so the remembered stage is only honoured while it is open.
  const openSection = (index) => {
    const remembered = lastStageBySection.current[index] || defaultStageFor(index);
    setActiveTab(`section_${index}_${stageIsOpen(index, remembered) ? remembered : 'line'}`);
  };
  const openStage = (index, stage) => {
    lastStageBySection.current[index] = stage;
    setActiveTab(`section_${index}_${stage}`);
  };
  // The stage a kept-mounted section body renders. The active section follows the tab bar (activeStage); a
  // hidden one holds the stage it was left on, so it is already showing the right view when shown again.
  const sectionStage = (index) => {
    if (index === activeSectionIndex) { return activeStage; }
    const remembered = lastStageBySection.current[index] || defaultStageFor(index);
    return stageIsOpen(index, remembered) ? remembered : 'line';
  };

  // The stage stepper hangs UNDER its parent section tab, like a drop-down sub-menu, rather than floating
  // left. Since the active section tab's horizontal position depends on the labels before it, we measure it:
  // activeTabRef is put on the active section's top tab, tabRowRef on the tab row (which shares the container's
  // left edge with the stepper row below the divider), and the stepper is indented by the gap between them --
  // then clamped between codeRef's width (so it never slides over the drawing code on that row's left) and a
  // cap from stageTabsRef / sourceRef widths (so the indent never pushes the source control, on the row's
  // right, onto a second line under a far-right active tab).
  const tabRowRef = useRef(null);
  const activeTabRef = useRef(null);
  const stageTabsRef = useRef(null);
  const sourceRef = useRef(null);
  const codeRef = useRef(null);
  const [subMenuOffset, setSubMenuOffset] = useState(0);

  // The guided setup wizard auto-opens on a quote that has not been set up yet -- nothing priced -- so a fresh
  // quote is walked through quantity -> pick a line -> pick a solution per section. A quote that already has a
  // priced section opens straight to the page; the "Guided setup" button reopens the wizard any time. While it
  // is open the active tab's section body is not rendered, so the wizard's own ManufacturingLineLists is the
  // only one on screen (two would fight over the manufacturing_line_<index> URL state); closing it re-renders
  // the section, which reads the now-priced state the wizard persisted.
  //
  // "Not set up yet" is not the same as "nothing selected right now". Changing a manufacturing line discards
  // the solution that was chosen on the old one, which can leave a quote with nothing priced -- and a user who
  // has just made a choice and refreshed should land on their listing, not be walked through a wizard they
  // finished days ago. A picked manufacturing line in the address bar is what tells the two apart: it means
  // this quote is mid-flow, in the hands of someone who is choosing for themselves.
  const anySectionPriced = (window.user_quote_nav_sections || [])
    .some((section) => section.selected_lists && section.selected_lists.length > 0);
  const anySectionMidFlow = priceableSections
    .some(({ index }) => readSelectedManufacturingLineList(index) !== null);
  const [wizardOpen, setWizardOpen] = useState(
    sectionTabs.length > 0 && !anySectionPriced && !anySectionMidFlow
  );

  // Closing the wizard FOLLOWS THE USER TO THE PRICE. The wizard reports the section it priced -- the one the
  // user just picked a manufacturing line and a solution for -- so the page opens that section's Pricing tab
  // instead of dropping the user back on the Manufacturing Line tab it happened to be sitting on, which would
  // ask them to make a choice they have already made. A wizard dismissed before it priced anything reports no
  // section, and leaves the active tab alone.
  const closeWizard = ({ pricedSectionIndex } = {}) => {
    setWizardOpen(false);
    if (pricedSectionIndex != null) { openStage(pricedSectionIndex, 'pricing'); }
  };

  useEffect(() => { window.user_quote_id = user_quote_id; }, []);

  // NOTHING RENDERS UNTIL THE PERSISTED ANSWERS ARE BACK IN THE CACHE. The sections below start fetching the
  // moment they mount, and the cache they consult is read synchronously -- so a section mounted before
  // hydration would miss every entry and re-list, re-solve and re-score a quote the browser already has the
  // answers for. Gating here, at the one component the whole /mcc page hangs off, is what lets every reader
  // downstream stay exactly as it was.
  //
  // The wait is milliseconds against the minutes of scoring it saves, and it is bounded twice over: whichever
  // of hydration or the timeout comes first releases the page, and a page with no fingerprint has nothing to
  // wait FOR (it persists nothing), so it renders at once exactly as it always did.
  const [responseCacheHydrated, setResponseCacheHydrated] = useState(
    () => !window.frank_response_cache_fingerprint
  );
  useEffect(() => {
    let released = false;
    const release = () => { if (!released) { released = true; setResponseCacheHydrated(true); } };

    hydrateResponseCache({
      // Absent on any page that does not render one, which turns persistence off and leaves the in-memory
      // cache behaving exactly as it did before.
      fingerprint: window.frank_response_cache_fingerprint,
      // WHICH viewing of those answers this is (language + unit). Held apart from the fingerprint so the
      // viewings coexist instead of evicting one another -- see Frank::ResponseCacheFingerprint#variant.
      variant: window.frank_response_cache_variant,
      // WHOSE they are. The store lives in the browser profile, so a load by anyone else deletes what is not
      // theirs rather than leaving one customer's machines and prices in another's browser.
      userId: window.frank_response_cache_user_id,
      quoteId: user_quote_id
    }).then(release).catch(release);

    const timer = setTimeout(release, CACHE_HYDRATION_TIMEOUT_MILLISECONDS);
    return () => { clearTimeout(timer); };
  }, []);

  // A REAL order-count change drops every section's selection via resetAllSectionSelections (above -- the routine
  // shared with the source switch, which carries the "why"), then bumps orderCountRevision to remount the open
  // section so it re-seeds from the cleared state; the server reset rides the count field's own general-form
  // persist (quote_builder_user_quote_general_form_update). Guarded against a no-op re-publish -- DelayedNumberFormField
  // re-fires count_changed on mount with the seeded value -- so reopening a quote never wipes its saved selection.
  MyEventListener([
    { key: 'quote_builder.count_changed', callback: (msg) => {
      const nextCount = Number(msg.detail);
      if (Number.isFinite(nextCount) && nextCount > 0 && nextCount !== Number(count)) {
        resetAllSectionSelections();
        setOrderCountRevision((revision) => revision + 1);
      }
      setCount(() => msg.detail);
    } }
  ]);

  const readOnly = window.user_quote_finalised;

  const isSectionTab = activeSectionIndex !== null;

  // The quote-wide manufacturing-lines source belongs only on the two stages where the CHOICE OF ACCOUNT still
  // changes what is on screen: the Manufacturing Line listing (which lines the section is offered) and the
  // Solution listing (which solutions those lines yield). By the Pricing stage a line and a solution are already
  // chosen, and on the global tabs (Manage / Customer / Dieline / Total / General) there is nothing
  // account-specific to re-solve -- so the control is hidden there rather than offered where it does nothing.
  const showManufacturingLinesSource = isSectionTab && (activeStage === 'line' || activeStage === 'solution');

  // How wide a GLOBAL tab body is drawn. A FORM tab is held to a readable, centred ~620px column so its labels
  // sit near their inputs; the Dieline tab gets the full container width for its drawing. The GENERAL tab is
  // the exception: it renders through QuoteBuilderNavSection, which insets its form into a Bootstrap col-lg-8
  // (8 of 12 columns), so its column is widened by 12/8 -- 620 -> 930 -- to land that inset form at the SAME
  // ~620px the other form tabs (Manage / Customer) fill directly, keeping every form tab one width. Section
  // bodies are not sized here -- they take the full container width for their cards.
  const globalTabStyle = (key) => {
    if (key === 'dieline') { return undefined; }
    if (key === 'general') { return { maxWidth: '930px', margin: '0 auto' }; }
    return { maxWidth: '620px', margin: '0 auto' };
  };

  // The sentence for the marker on a LINKED section's Manufacturing Line tab, or null when this section chooses
  // for itself. Naming the section that leads is the useful half -- "somewhere else" would leave the reader
  // hunting -- so the unnamed wording is only for a stale payload whose parent is no longer in the list.
  const linkedParentSection = isSectionTab
    ? linkedParentOf(window.user_quote_nav_sections, activeSectionIndex)
    : null;
  const linkedSectionNote = (() => {
    if (!isSectionTab) { return null; }
    if (linkedParentSection) {
      return t('quote_builder.frank_linked_section_body', { section: linkedParentSection.label });
    }
    const section = (window.user_quote_nav_sections || [])
      .find((candidate) => candidate.sort_order === activeSectionIndex);
    return section && section.solution_user_quote_nav_section_id
      ? t('quote_builder.frank_linked_section_body_unnamed')
      : null;
  })();

  useLayoutEffect(() => {
    if (!isSectionTab || !activeTabRef.current || !tabRowRef.current) {
      setSubMenuOffset(0);
      return undefined;
    }
    let cancelled = false;
    const measure = () => {
      // The observer, the resize listener, and fonts.ready can all fire after the effect is torn down (unmount
      // or a tab switch), when the refs may be gone -- bail rather than read null or set state on a dead run.
      if (cancelled || !tabRowRef.current || !activeTabRef.current) { return; }
      const tabRow = tabRowRef.current.getBoundingClientRect();
      const idealOffset = Math.max(0, activeTabRef.current.getBoundingClientRect().left - tabRow.left);
      // The stepper row shares the tab row's width, so tabRow.width is the budget. Keep the stepper clear of the
      // drawing code on the row's left (codeWidth + 16), and cap it so the stepper plus the source still fit
      // (16px breathing room); the code floor wins when the row is too narrow for all three, letting the source
      // wrap rather than the stepper cover the code.
      const stepperWidth = stageTabsRef.current ? stageTabsRef.current.getBoundingClientRect().width : 0;
      const sourceWidth = sourceRef.current ? sourceRef.current.getBoundingClientRect().width : 0;
      const codeWidth = codeRef.current ? codeRef.current.getBoundingClientRect().width : 0;
      const minOffset = codeWidth > 0 ? codeWidth + 16 : 0;
      const maxOffset = Math.max(minOffset, tabRow.width - stepperWidth - sourceWidth - 16);
      setSubMenuOffset(Math.max(minOffset, Math.min(idealOffset, maxOffset)));
    };
    measure();
    // Observe the tab row so the stepper re-aligns on any width change -- not just a window resize, but a
    // sidebar or scrollbar appearing or the container reflowing. The measure runs in a rAF so state is not set
    // straight from the observer callback (which browsers flag as a ResizeObserver loop). Fall back to the
    // resize event where ResizeObserver is unavailable (jsdom).
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => window.requestAnimationFrame(measure));
      observer.observe(tabRowRef.current);
    } else {
      window.addEventListener('resize', measure);
    }
    // Web fonts change the tab labels' widths (and so the active tab's left) without changing the row's width,
    // which the observer would not catch -- re-measure once they have loaded.
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(measure); }
    return () => {
      cancelled = true;
      if (observer) { observer.disconnect(); }
      window.removeEventListener('resize', measure);
    };
    // responseCacheHydrated belongs here with the tab: until it is true this component renders null, so the
    // tabs this measures are not in the document yet and there is nothing to measure against. Without it the
    // measurement only ever ran against that empty first render, and the stepper stayed pinned to the far left
    // of the row instead of hanging under its own section tab.
  }, [activeTab, isSectionTab, responseCacheHydrated]);

  // ONE global (non-section) tab's body, by key -- Manage / Total / Customer / General / Dieline. Rendered by
  // key (not by reading activeTab) because these bodies are kept mounted like the section bodies (see the
  // render below), so more than one can be in the tree at once. The width each wants sits in globalTabStyle.
  const renderGlobalTab = (tabKey) => {
    if (tabKey === 'manage') {
      return (
        <>
          <QuoteBuilderForm config={config.general} readOnly={readOnly} />
          <QuoteBuilderManagePanel readOnly={readOnly} section="status" />
        </>
      );
    }
    if (tabKey === 'total') {
      return <QuoteBuilderManagePanel readOnly={readOnly} section="summary" />;
    }
    if (tabKey === 'customer') {
      return <QuoteBuilderForm config={config.customer} readOnly={readOnly} />;
    }
    if (tabKey === 'general' && generalSection) {
      // The general section's quote-wide pricing sub-sections (Profit Margin, Transport, ...). Rendered
      // straight through QuoteBuilderNavSection -- whose inner QuoteBuilder fetches those sub-sections from the
      // server -- rather than the legacy GeneralNavSection, which only renders once it receives a
      // nav_section_general_part_type event fired by SelectedList (a component Frank never mounts, so the event
      // never comes). An empty payload is safe: a general section has no parts or members, so
      // QuoteBuilderNavSection takes navSection.drawing as its item and never reads payload.items. noTitle
      // because the "General" tab already names the section, so the inner heading would just repeat it.
      return (
        <QuoteBuilderNavSection
          index={generalSection.index}
          config={config}
          payload={{}}
          presentedOptions={{}}
          navSection={generalSection}
          noTitle
        />
      );
    }
    if (tabKey === 'dieline') {
      // Show the dieline SVG (rendered server-side with its dimension texts) above the material +
      // dimension table, so the user can see the dieline being priced. dieline_svg is inline SVG markup.
      return (
        <>
          {dieline_svg && (
            <div
              style={{ maxWidth: '930px', margin: '0 auto 15px' }}
              dangerouslySetInnerHTML={{ __html: dieline_svg }}
            />
          )}
          <div style={{ maxWidth: '620px', margin: '0 auto' }}>
            <PresentedDielineOptions />
          </div>
        </>
      );
    }

    // İç sıvama (Inside lamination): a PRICING-ONLY tab (no manufacturing line, no solution) that sits right
    // after Hard cardboard. It shows the single material dimension it inherits from the hard_cardboard section
    // it follows (the tab is enabled only once that section is solved).
    if (tabKey.startsWith('inside_lamination_')) {
      const index = Number(tabKey.replace('inside_lamination_', ''));
      return (
        <InsideLaminationBody
          config={config}
          navSection={navSections[index]}
          userQuoteId={user_quote_id}
          sectionIndex={index}
          unit={unit}
        />
      );
    }

    return null;
  };

  // ONE priceable section's body, rendered once and kept mounted (see visitedSections) so leaving its tab and
  // coming back does not rebuild it. A single-section list, so the section's own Frank-mode material-sheet
  // fetch + gating are reused. The KEY is the section (not the stage), so the three stages share ONE
  // ManufacturingLineLists instance -- the picked plan and its solved solutions carry across stages; only
  // stageView changes. Picking a manufacturing line or a solution stays on its own stage; the user moves on
  // with the stage tabs. Visiting the Pricing stage commits + prices the marked solution.
  const renderSectionBody = (index) => {
    // The stage AFTER the one on screen, in the section's fixed order (Manufacturing Line -> Solution ->
    // Pricing). The card's "Next" button walks the user there; the Pricing stage has no next, so it is null and
    // no Next button is offered on the priced view.
    const stage = sectionStage(index);
    const stageOrder = sectionStageTabs.map((stageTab) => stageTab.stage);
    const nextStage = stageOrder[stageOrder.indexOf(stage) + 1] || null;
    return (
      <LayoutNavSectionList
        // The source and orderCountRevision are in the key so switching the source or changing the order count
        // REMOUNTS the section, which re-seeds its state and re-fetches against the new problem (the URL builders
        // read window.frank_manufacturing_lines_source; the count rides the fetch params).
        key={`section_${index}_${manufacturingLinesSource}_${machinesRevision}_${orderCountRevision}`}
        // Bumped by a tab return that found a real change -- the section's data fetches depend on it, so they
        // re-run IN PLACE (no remount, no blink). NOT in the key above, unlike machinesRevision.
        dataRevision={dataRevision}
        navSections={[navSections[index]]}
        count={count}
        config={config}
        stageView={stage}
        // What a USER click answered, so the stage after it opens straight away rather than on the next
        // render that happens to re-read the address bar. Restores do not fire these -- the seeds above
        // cover those.
        onLinePicked={(identifier) => setPickedLineBySection((picked) => (
          picked[index] === identifier
            ? picked
            : { ...picked, [index]: identifier }
        ))}
        onSolutionPicked={(group) => setChosenSolutionBySection(
          (chosen) => ({ ...chosen, [index]: Boolean(group) })
        )}
        onGoToNextStage={nextStage ? () => openStage(index, nextStage) : null}
        onGoToManufacturingLine={() => openStage(index, 'line')}
        // The owner's own lines came up empty and they borrowed a standard one: flip the quote to Standard,
        // keep this section on that line, and open its solutions (see useStandardLineForSection).
        onUseStandardLine={(identifier) => useStandardLineForSection(index, identifier)}
      />
    );
  };

  // After every hook, so the rules of hooks are kept: the page holds still for the moment it takes to read its
  // cached answers back (see the hydration effect), rather than mounting sections that would re-ask for them.
  if (!responseCacheHydrated) { return null; }

  return (
    <ErrorBoundary componentName="FrankQuoteBuilderPage" errorContext={{ user_quote_id: user_quote_id }}>
      <CacheProvider initialCache={window.user_quote_section_properties_cached}>
        <LayoutContext.Provider value={LayoutContextValues}>
          <DebugPopupProvider>
          <div className={`${styles.component_container} container`}>
            {/* TOP LEVEL, ABOVE THE DIVIDER: the parent model group on the left, one tab per section plus
                Manage / Customer / Dieline centred, and the "Guided setup" button on the right. Aligning to
                flex-end sits the tab underlines on the border. Using the shared .dct-tabs / .dct-tab
                design-language classes (brand underline on the active tab) rather than a per-page inline style.
                tabRowRef marks the row's left edge so the stage stepper below can hang under the active tab. */}
            <div ref={tabRowRef} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', borderBottom: '2px solid var(--rule)' }}>
              {/* The parent model group and the group name stack on the tab row's LEFT, above the divider, with
                  the group name on the divider (paddingBottom matches the Guided-setup button). Only the drawing
                  code is below the divider. */}
              {header && (
                <div className="page-in-name" style={{ fontSize: '15px', margin: 0, paddingBottom: '8px', whiteSpace: 'nowrap', lineHeight: 1.6 }}>
                  <div>
                    <span>
                      {header.demo
                        ? header.demoLabel
                        : <a href={header.parentUrl}>{header.parentName}</a>}
                    </span>
                  </div>
                  {/* The group name is the page's h1 (as it was before the header split into rows). The shared
                      `.page-in-name h1` rule makes it inline at 23px, so override it back to a block line at the
                      row's own 15px and drop the default h1 margin, so it looks identical to the line above. */}
                  <h1 style={{ fontSize: 'inherit', display: 'block', margin: 0 }}>
                    <a href={header.groupUrl}>{header.groupName}</a>
                  </h1>
                </div>
              )}
              <div className="dct-tabs" style={{ flex: 1, border: 'none', justifyContent: 'center', flexWrap: 'wrap' }}>
                {topTabs.map((tab) => {
                  const isActive = tab.sectionIndex != null
                    ? activeSectionIndex === tab.sectionIndex
                    : activeTab === tab.key;
                  const isDisabled = tab.insideLaminationIndex != null
                    && !insideLaminationEnabled(tab.insideLaminationIndex);
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      ref={isActive && tab.sectionIndex != null ? activeTabRef : null}
                      className={`dct-tab${isActive ? ' is-active' : ''}`}
                      style={{ marginBottom: '-2px' }}
                      disabled={isDisabled}
                      title={isDisabled ? t('quote_builder.frank_inside_lamination_locked') : undefined}
                      onClick={() => (tab.sectionIndex != null ? openSection(tab.sectionIndex) : setActiveTab(tab.key))}
                    >
                      {tab.label}
                      {/* A brand caret pointing DOWN on the Total tab once it carries a figure, so
                          "Toplam (USD 47.66)" reads as a clickable tab that opens the breakdown below, not a
                          static readout. Decorative (aria-hidden): the tab's accessible name stays the label. */}
                      {tab.key === 'total' && totalPresented && (
                        <i className="fa fa-caret-down" aria-hidden="true" style={{ marginLeft: '6px', color: 'var(--brand)' }} />
                      )}
                    </button>
                  );
                })}
              </div>
              {/* Reopen the guided setup wizard any time; only shown when there is a part section to set up. */}
              {sectionTabs.length > 0 && (
                <div style={{ paddingBottom: '8px', paddingLeft: '12px' }}>
                  <button
                    type="button"
                    className="dct-button dct-button--secondary dct-button--small"
                    onClick={() => setWizardOpen(true)}
                  >
                    {t('setup_wizard.open')}
                  </button>
                </div>
              )}
            </div>

            {/* BELOW THE DIVIDER, one band that fills the width: the drawing code on the LEFT (absolutely placed
                so it costs no flex width), the stage stepper indented under the active section tab, and the
                quote-wide source on the RIGHT. Keeping the code beside the stepper/source -- rather than on a
                row of its own above -- uses the space that was otherwise empty to its right. */}
            <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', margin: '10px 0 0', minHeight: '34px' }}>
              {header && (
                <div ref={codeRef} className="page-in-name" style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', fontSize: '15px', margin: 0, whiteSpace: 'nowrap' }}>
                  <a href={header.catalogUrl}>{header.catalogName}</a>
                </div>
              )}
              {isSectionTab && (
                <div ref={stageTabsRef} className={frankStyles.stageTabs} role="tablist" aria-label={t('quote_builder.frank_section_stages')} style={{ marginLeft: subMenuOffset }}>
                  {sectionStageTabs.map((stageTab, stepIndex) => {
                    const isActive = activeStage === stageTab.stage;
                    // Closed until the stage before it has been answered -- see stageIsOpen. The title says
                    // WHY, so a disabled tab is an instruction rather than a dead end.
                    const isOpen = stageIsOpen(activeSectionIndex, stageTab.stage);
                    return (
                      <button
                        key={stageTab.stage}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        disabled={!isOpen}
                        title={isOpen ? undefined : t(`quote_builder.frank_stage_locked_${stageTab.stage}`)}
                        className={`${frankStyles.stageTab}${isActive ? ` ${frankStyles.stageTabActive}` : ''}`
                          + `${isOpen ? '' : ` ${frankStyles.stageTabLocked}`}`}
                        onClick={() => openStage(activeSectionIndex, stageTab.stage)}
                      >
                        <span className={frankStyles.stageNumber}>{stepIndex + 1}</span>
                        {t(stageTab.labelKey)}
                        {/* A LINKED section's manufacturing line comes from the section that leads it, so there
                            is nothing to choose on this stage. Said as a marker on the tab rather than a panel
                            above the answer: it is read once, and after that a block that size is only ever in
                            the way of what the reader came for. */}
                        {stageTab.stage === 'line' && linkedSectionNote ? (
                          <span className={frankStyles.stageTabInfo} data-frank-linked-tab-info>
                            {/* The glyph is decoration; the sentence below is the content, so it is the part a
                                screen reader should read. */}
                            <span aria-hidden="true">i</span>
                            <span className={frankStyles.stageTabTooltip} role="tooltip">
                              {linkedSectionNote}
                            </span>
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* The quote-wide manufacturing-lines source, on the RIGHT of this row (marginLeft:auto). Shown
                  only on the Manufacturing Line and Solution stages -- the two where switching the account
                  re-solves what is on screen; hidden on Pricing and the global tabs, where it would change
                  nothing. The stepper's indent is capped against this control's width so, while it is shown, the
                  two always share one line. */}
              {showManufacturingLinesSource && (
                <div ref={sourceRef} style={{ marginLeft: 'auto' }}>
                  <StandardManufacturingLinesToggle
                    value={manufacturingLinesSource}
                    onChange={changeManufacturingLinesSource}
                  />
                </div>
              )}
            </div>

            {/* Every tab body -- global tabs and section bodies -- renders from this one wrapper, each shown
                only while active and otherwise HIDDEN rather than unmounted (the "why" is on the visited-set
                states above). The .filter mounts the active body too, not only the visited ones, so it appears
                on the first click without waiting a frame for the effect to record the visit.

                Hidden with visibility + out of flow, NOT display:none: a section's priced-view previews
                (LayoutSvg) fit an svg-pan-zoom instance to the container's MEASURED size on a repeating timer,
                and a display:none container measures 0 -- the sheets collapse to a black square and do not
                recover when shown again. visibility:hidden keeps the real width; position:absolute keeps the
                hidden ones from leaving a blank gap under the active one. A form tab gets a readable centred
                column (globalTabStyle), a section body the full container width for its cards.

                Nothing is mounted while the wizard is open, so only its own ManufacturingLineLists is on screen
                (two would fight over the manufacturing_line_<index> URL state); closing it mounts the section. */}
            {!wizardOpen && (
              <div style={{ position: 'relative' }}>
                {globalTabKeys
                  .filter((key) => visitedGlobalTabs.has(key) || activeTab === key)
                  .map((key) => (
                    <div
                      key={`global_tab_${key}`}
                      data-frank-global-tab={key}
                      style={activeTab === key
                        ? globalTabStyle(key)
                        : { position: 'absolute', top: 0, left: 0, right: 0, visibility: 'hidden' }}
                    >
                      {renderGlobalTab(key)}
                    </div>
                  ))}
                {priceableSections
                  .filter(({ index }) => visitedSections.has(index) || index === activeSectionIndex)
                  .map(({ index }) => (
                    <div
                      key={`section_body_${index}`}
                      data-frank-section-body={index}
                      style={activeSectionIndex === index
                        ? undefined
                        : { position: 'absolute', top: 0, left: 0, right: 0, visibility: 'hidden' }}
                    >
                      {renderSectionBody(index)}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {wizardOpen && (
            <FrankSetupWizard
              navSections={navSections}
              config={config}
              onClose={closeWizard}
              manufacturingLinesSource={manufacturingLinesSource}
              onManufacturingLinesSourceChange={changeManufacturingLinesSource}
              // Borrowing a standard line from the empty-state fallback flips the quote to Standard, keeps that
              // section on the borrowed line and opens its solutions -- the same handler the page tabs use.
              onUseStandardLineForSection={useStandardLineForSection}
              machinesRevision={machinesRevision}
              dataRevision={dataRevision}
            />
          )}
          </DebugPopupProvider>
        </LayoutContext.Provider>
      </CacheProvider>
    </ErrorBoundary>
  );
};

export default FrankQuoteBuilderPage;
